import React, { useState, useEffect, useRef } from 'react';
import apiClient from '../../apiClient';
import './EditContact.css';

const ContactEditForm = ({ contact, onSuccess, onCancel, statusOptions = [], statusLoading, statusError, sourceOptions = [], sourceLoading, sourceError }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    source: '',
    company_id: '',
    company_name: '',
    company_industry: '',
    company_website: '',
    company_city: '',
    company_state: '',
    company_country: '',
    notes: '',
    status: '' // Added status field
  });

  const [companies, setCompanies] = useState([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editCompanyDetails, setEditCompanyDetails] = useState(false);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const [isSavingCompany, setIsSavingCompany] = useState(false);

  // Store the original company
  const originalCompany = useRef(null);

  useEffect(() => {
    if (contact) {
      console.log('contact',contact)
      setFormData({
        name: contact.name || '',
        email: contact.email || '',
        phone: contact.phone || '',
        source: contact.source || '',
        company_id: contact.company_id || '',
        company_name: contact.company_name || '',
        company_industry: contact.company_industry || '',
        company_website: contact.company_website || '',
        company_city: contact.company_city || '',
        company_state: contact.company_state || '',
        company_country: contact.company_country || '',
        notes: contact.notes || '',
        status: contact.status || '' // Initialize status from contact
      });
      setCompanySearch(contact.company_name || '');

      // Store the original company data
      if (contact.company_id) {
        originalCompany.current = {
          id: contact.company_id,
          name: contact.company_name,
          industry: contact.company_industry,
          website: contact.company_website,
          city: contact.company_city,
          state: contact.company_state,
          country: contact.company_country
        };
      }
    }
    fetchCompanies();
  }, [contact]);

  const fetchCompanies = async () => {
    setIsLoadingCompanies(true);
    try {
      const response = await apiClient.get('/api/companies');
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setIsLoadingCompanies(false);
    }
  };

  const filteredCompanies = companies.filter(company => 
    company.name.toLowerCase().includes(companySearch.toLowerCase())
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCompanySearchChange = (e) => {
    setCompanySearch(e.target.value);
    setShowCompanyDropdown(true);
  };

  const handleCompanySelect = (company) => {
    setFormData(prev => ({
      ...prev,
      company_id: company.id,
      company_name: company.name,
      company_industry: company.industry || '',
      company_website: company.website || '',
      company_city: company.city || '',
      company_state: company.state || '',
      company_country: company.country || ''
    }));
    setCompanySearch(company.name);
    setShowCompanyDropdown(false);
    setEditCompanyDetails(false);
  };
  const handleSaveCompany = async () => {
    if (!formData.company_name) {
      setErrors(prev => ({ ...prev, company_name: 'Company name is required' }));
      return;
    }

    setIsSavingCompany(true);
    try {
      const companyPayload = {
        name: formData.company_name,
        industry: formData.company_industry,
        website: formData.company_website,
        city: formData.company_city,
        state: formData.company_state,
        country: formData.company_country
      };

      let response;
      if (formData.company_id) {
        // Update existing company
        response = await apiClient.put(`/api/companies/${formData.company_id}`, companyPayload);
      } else {
        // Create new company
        response = await apiClient.post('/api/companies', companyPayload);
      }

      // Update the form with the saved company data
      setFormData(prev => ({
        ...prev,
        company_id: response.data.id,
        company_name: response.data.name,
        company_industry: response.data.industry || '',
        company_website: response.data.website || '',
        company_city: response.data.city || '',
        company_state: response.data.state || '',
        company_country: response.data.country || ''
      }));

      // Refresh companies list
      await fetchCompanies();
      setEditCompanyDetails(false);
    } catch (error) {
      console.error('Error saving company:', error);
      alert(error.response?.data?.message || 'Failed to save company');
    } finally {
      setIsSavingCompany(false);
    }
  };

  const toggleEditCompanyDetails = () => {
    setEditCompanyDetails(!editCompanyDetails);
  };

  const validateForm = () => {
    const newErrors = {};
    const trimmedName = formData.name.trim();
    const trimmedEmail = formData.email.trim();
    const trimmedPhone = formData.phone.trim();

    if (!trimmedName) {
      newErrors.name = 'Name is required';
    }
    
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      newErrors.email = 'Invalid email format';
    }
    
    if (trimmedPhone && !/^[0-9+\-\s]+$/.test(trimmedPhone)) {
      newErrors.phone = 'Invalid phone number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        email: formData.email || null,
        phone: formData.phone || null,
        company_id: formData.company_id || null,
        status: formData.status || null
      };

      const response = await apiClient.put(`/api/contacts/${contact.id}`, payload);
      onSuccess(response.data);
    } catch (error) {
      console.error('Error updating contact:', error);
      
      if (error.response?.data?.errors) {
        const serverErrors = {};
        Object.keys(error.response.data.errors).forEach(key => {
          serverErrors[key] = error.response.data.errors[key][0];
        });
        setErrors(serverErrors);
      } else {
        alert(error.response?.data?.message || 'Failed to update contact');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCompanyDropdown = () => {
    // Always include original company first if it exists
    const companiesToShow = [...filteredCompanies];
    
    if (originalCompany.current && 
        !companiesToShow.some(c => c.id === originalCompany.current.id)) {
      companiesToShow.unshift(originalCompany.current);
    }

    return (
      <div className="company-dropdown">
        {/* Original company always first */}
        {originalCompany.current && (
          <div 
            key={`original-${originalCompany.current.id}`}
            className={`company-option ${
              formData.company_id === originalCompany.current.id ? 'current-company' : 'original-company'
            }`}
            onClick={() => handleCompanySelect(originalCompany.current)}
          >
            {originalCompany.current.name} (Original)
          </div>
        )}

        {/* Other companies */}
        {companiesToShow
          .filter(c => !originalCompany.current || c.id !== originalCompany.current.id)
          .map(company => (
            <div 
              key={company.id} 
              className={`company-option ${
                formData.company_id === company.id ? 'current-company' : ''
              }`}
              onClick={() => handleCompanySelect(company)}
            >
              {company.name} - {company.industry || company.city || 'No info'}
            </div>
          ))
        }

        {/* Add new option */}
        {filteredCompanies.length === 0 && (
          <div 
            className="company-option add-new"
            onClick={() => {
              setFormData(prev => ({
                ...prev,
                company_name: companySearch,
                company_id: '' // Clear ID for new company
              }));
              setEditCompanyDetails(true);
              setShowCompanyDropdown(false);
            }}
          >
            + Add New Company: "{companySearch}"
          </div>
        )}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} className="contact-edit-form">
      <div className="form-section">
        <h3>Personal Information</h3>
        
        <div className="form-group">
          <label htmlFor="name">Name*</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={errors.name ? 'error' : ''}
          />
          {errors.name && <span className="error-message">{errors.name}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={errors.email ? 'error' : ''}
          />
          {errors.email && <span className="error-message">{errors.email}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="phone">Phone</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={errors.phone ? 'error' : ''}
          />
          {errors.phone && <span className="error-message">{errors.phone}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="source">Source</label>
          <select
            id="source"
            name="source"
            value={formData.source}
            onChange={handleChange}
            disabled={sourceLoading}
          >
            <option value="">Select source</option>
            {sourceOptions.map((source) => (
              <option key={source} value={source}>
                {source}
              </option>
            ))}
          </select>
          {sourceError && <span className="error-message">Failed to load source options</span>}
        </div>

        {/* <div className="form-group">
          <label htmlFor="source">Source</label>
          <input
            type="text"
            id="source"
            name="source"
            value={formData.source}
            onChange={handleChange}
            placeholder="How did you find this contact?"
          />
        </div> */}

        <div className="form-group">
          <label htmlFor="status">Status</label>
          <select
            id="status"
            name="status"
            value={formData.status}
            onChange={handleChange}
            disabled={statusLoading}
          >
            <option value="">Select status</option>
            {statusOptions.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
          {statusError && <span className="error-message">Failed to load status options</span>}
        </div>
      </div>

      <div className="form-section">
      <div className="section-header">
    <h3>Company Information</h3>
    <div className="company-actions">
      {(formData.company_id || editCompanyDetails) && (
        <button
          type="button"
          onClick={toggleEditCompanyDetails}
          className="btn edit-company-btn"
        >
          {editCompanyDetails ? 'Cancel Editing' : 'Edit Details'}
        </button>
      )}
      {editCompanyDetails && (
        <button
          type="button"
          onClick={handleSaveCompany}
          className="btn save-company-btn"
          disabled={isSavingCompany}
        >
          {isSavingCompany ? 'Saving...' : 'Save Company'}
        </button>
      )}
    </div>
  </div>
        
        <div className="form-group">
          <label>Company</label>
          <input
            type="text"
            value={companySearch}
            onChange={handleCompanySearchChange}
            onFocus={() => setShowCompanyDropdown(true)}
            placeholder="Search companies..."
            className="company-search-input"
          />
          {showCompanyDropdown && renderCompanyDropdown()}
        </div>

        {(formData.company_id || editCompanyDetails) && (
          <>
            <div className="form-group">
              <label htmlFor="company_name">Company Name</label>
              <input
                type="text"
                id="company_name"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                disabled={!editCompanyDetails && formData.company_id}
              />
            </div>

            <div className="form-group">
              <label htmlFor="company_industry">Industry</label>
              <input
                type="text"
                id="company_industry"
                name="company_industry"
                value={formData.company_industry}
                onChange={handleChange}
                disabled={!editCompanyDetails && formData.company_id}
              />
            </div>

            <div className="form-group">
              <label htmlFor="company_website">Website</label>
              <input
                type="url"
                id="company_website"
                name="company_website"
                value={formData.company_website}
                onChange={handleChange}
                placeholder="https://example.com"
                disabled={!editCompanyDetails && formData.company_id}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="company_city">City</label>
                <input
                  type="text"
                  id="company_city"
                  name="company_city"
                  value={formData.company_city}
                  onChange={handleChange}
                  disabled={!editCompanyDetails && formData.company_id}
                />
              </div>

              <div className="form-group">
                <label htmlFor="company_state">State/Province</label>
                <input
                  type="text"
                  id="company_state"
                  name="company_state"
                  value={formData.company_state}
                  onChange={handleChange}
                  disabled={!editCompanyDetails && formData.company_id}
                />
              </div>

              <div className="form-group">
                <label htmlFor="company_country">Country</label>
                <input
                  type="text"
                  id="company_country"
                  name="company_country"
                  value={formData.company_country}
                  onChange={handleChange}
                  disabled={!editCompanyDetails && formData.company_id}
                />
              </div>
            </div>
          </>
        )}
      </div>

      <div className="form-section">
        <h3>Additional Information</h3>
        <div className="form-group">
          <label htmlFor="notes">Notes</label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows="4"
          />
        </div>
      </div>

      <div className="form-actions">
        <button
          type="button"
          onClick={onCancel}
          className="btn cancel-btn"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn submit-btn"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Updating...' : 'Update Contact'}
        </button>
      </div>
    </form>
  );
};

export default ContactEditForm;
